import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ContadorService } from '../../../services/contador/contador.service';
import { DespesaAcessoriaService } from '../../../services/contador/despesa-acessoria.service';
import { SolicitacaoContratoRecebidaDTO } from '../../../models/contador/contador.model';
import { DespesaAcessoriaPendenteDTO } from '../../../models/contador/despesa-acessoria.model';
import { ehIvaTipoDespesa } from '../../../shared/utils/despesa-contador.util';

/**
 * Card do dashboard que resume as pendencias do contador para o CLIENTE:
 *
 * - Solicitacoes de contrato do contador aguardando aceite/recusa
 *   (GET /contador/contratos/solicitacoes-recebidas).
 * - Despesas acessorias pendentes de aprovacao, com destaque para as de
 *   tipo de gasto IVA (GET /cliente/despesas-contador/pendentes).
 *
 * O card e AUTOSSUFICIENTE: busca os proprios dados e some quando nao ha
 * nenhuma pendencia (`temPendencias === false`), evitando poluir o dashboard
 * de quem nao tem contador. Ao clicar, navega para a tela de detalhes
 * (`/contador/notificacoes`) onde o cliente aceita/aprova cada item.
 *
 * Segue o padrao dos demais subcomponentes do dashboard: OnPush + objeto
 * `texts` recarregado no `onLangChange` + `cdr.markForCheck()`.
 */
@Component({
  selector: 'app-dashboard-contador-notificacoes',
  templateUrl: './dashboard-contador-notificacoes.component.html',
  styleUrls: ['./dashboard-contador-notificacoes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardContadorNotificacoesComponent implements OnInit, OnDestroy {
  /** Nº de solicitacoes de contrato pendentes (aguardando aceite do cliente). */
  contratosPendentes = 0;

  /** Nº total de despesas acessorias pendentes de aprovacao. */
  despesasPendentes = 0;

  /** Nº de despesas pendentes cujo tipo de gasto e IVA (subconjunto do total). */
  despesasIva = 0;

  /** True apos a primeira carga concluir (evita "piscar" o card antes do fetch). */
  carregado = false;

  /** Rotulos traduzidos (padrao dos cards do dashboard). */
  texts = {
    titulo: '',
    subtitulo: '',
    contratos_label: '',
    despesas_label: '',
    ver_detalhes: '',
  };

  private langChangeSub?: Subscription;

  constructor(
    private contadorService: ContadorService,
    private despesaService: DespesaAcessoriaService,
    private router: Router,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.cdr.markForCheck();
    });
    this.carregar();
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  /** Indica se ha ao menos uma pendencia (contrato ou despesa). */
  get temPendencias(): boolean {
    return this.contratosPendentes > 0 || this.despesasPendentes > 0;
  }

  /** Total de pendencias (usado no badge do card). */
  get totalPendencias(): number {
    return this.contratosPendentes + this.despesasPendentes;
  }

  /** Navega para a tela de detalhes das notificacoes do contador.
   *  `origem=dashboard` faz a tela de destino exibir o botao "Voltar". */
  verDetalhes(): void {
    this.router.navigate(['/contador/notificacoes'], { queryParams: { origem: 'dashboard' } });
  }

  // ---------------------------------------------------------------------------
  // Carga de dados
  // ---------------------------------------------------------------------------

  /**
   * Busca em paralelo as solicitacoes de contrato e as despesas pendentes.
   * Erros sao tolerados (ex.: empresa sem contador): a chamada com falha
   * vira lista vazia e o card simplesmente nao aparece.
   */
  private carregar(): void {
    forkJoin({
      contratos: this.contadorService
        .listarSolicitacoesRecebidas()
        .pipe(catchError(() => of([] as SolicitacaoContratoRecebidaDTO[]))),
      despesas: this.despesaService
        .listarPendentes()
        .pipe(catchError(() => of([] as DespesaAcessoriaPendenteDTO[]))),
    }).subscribe({
      next: ({ contratos, despesas }) => {
        this.contratosPendentes = contratos?.length ?? 0;
        this.despesasPendentes = despesas?.length ?? 0;
        this.despesasIva = (despesas ?? []).filter((d) => ehIvaTipoDespesa(d.tipoDespesa)).length;
        this.carregado = true;
        this.cdr.markForCheck();
      },
      error: () => {
        this.contratosPendentes = 0;
        this.despesasPendentes = 0;
        this.despesasIva = 0;
        this.carregado = true;
        this.cdr.markForCheck();
      },
    });
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('notificacoes.card.titulo'),
      subtitulo: this.translate.instant('notificacoes.card.subtitulo'),
      contratos_label: this.translate.instant('notificacoes.card.contratos_label'),
      despesas_label: this.translate.instant('notificacoes.card.despesas_label'),
      ver_detalhes: this.translate.instant('notificacoes.card.ver_detalhes'),
    };
  }
}
